import React from 'react';

/**
 * Calculate Levenshtein distance and return both distance and alignment information
 * Returns { distance, matchedPositions } where matchedPositions are indices in str2 that matched str1
 */
function levenshteinDistanceWithAlignment(
  str1: string,
  str2: string
): { distance: number; matchedPositions: number[] } {
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Early exit for exact match
  if (str1 === str2) {
    return {
      distance: 0,
      matchedPositions: Array.from({ length: len1 }, (_, i) => i)
    };
  }
  
  // Early exit if one string is empty
  if (len1 === 0) return { distance: len2, matchedPositions: [] };
  if (len2 === 0) return { distance: len1, matchedPositions: [] };
  
  // Create matrix for dynamic programming
  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));
  
  // Initialize first row and column
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;
  
  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  // Trace back to find matched positions
  // matchedPositions are indices in str1 (the text substring) that matched str2 (the query)
  const matchedPositions: number[] = [];
  let i = len1;
  let j = len2;
  
  while (i > 0 && j > 0) {
    const current = matrix[i][j];
    const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
    
    // Check which operation led to this cell
    if (matrix[i - 1][j - 1] + cost === current) {
      // Match or substitution
      if (cost === 0) {
        // It was a match, record it (using i-1 as index in str1, the text substring)
        matchedPositions.unshift(i - 1);
      }
      i--;
      j--;
    } else if (matrix[i - 1][j] + 1 === current) {
      // Deletion in str1 (skip character in str1)
      i--;
    } else {
      // Insertion in str1 (skip character in str2)
      j--;
    }
  }
  
  return {
    distance: matrix[len1][len2],
    matchedPositions
  };
}

/**
 * Calculate Levenshtein distance between two strings (optimized for small strings)
 * Returns the minimum number of edits (insertions, deletions, substitutions) needed
 */
function levenshteinDistance(str1: string, str2: string): number {
  return levenshteinDistanceWithAlignment(str1, str2).distance;
}

/**
 * Find the best fuzzy match substring in text for the given query
 * Uses sliding window with edit distance <= maxEdits
 * Optimized for performance with early termination and window size limits
 * Returns { start, end, matchedChars } or null if no match
 */
function findBestFuzzyMatch(
  text: string,
  query: string,
  maxEdits = 1
): { start: number; end: number; matchedChars: number[] } | null {
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  const queryLen = query.length;
  const textLen = text.length;
  
  if (queryLen === 0 || textLen === 0) return null;
  
  let bestMatch: { start: number; end: number; matchedChars: number[]; score: number } | null = null;
  
  // Optimize window size bounds - for maxEdits: 1, we can limit the search
  // The matched substring can be at most queryLen + maxEdits characters (for insertions)
  // and at least queryLen - maxEdits characters (for deletions)
  const maxWindowSize = queryLen + maxEdits;
  const minWindowSize = Math.max(1, queryLen - maxEdits);
  
  // Try all possible substring positions (check every character to ensure no matches are missed)
  // Prioritize checking exact length first, then nearby sizes
  for (let start = 0; start < textLen; start++) {
    // Try window sizes in order of priority (exact length first, then nearby)
    const windowSizes = [queryLen, queryLen - 1, queryLen + 1, queryLen - 2, queryLen + 2];
    
    for (const windowSize of windowSizes) {
      if (windowSize < minWindowSize || windowSize > maxWindowSize || start + windowSize > textLen) {
        continue;
      }
      
      const substring = textLower.substring(start, start + windowSize);
      const { distance, matchedPositions } = levenshteinDistanceWithAlignment(substring, queryLower);
      
      if (distance <= maxEdits) {
        // Calculate match quality score (lower distance and exact length is better)
        const lengthDiff = Math.abs(windowSize - queryLen);
        const score = distance * 1000 + lengthDiff; // Distance is primary factor
        
        if (!bestMatch || score < bestMatch.score) {
          // Convert matched positions from substring indices to text indices
          const matchedChars = matchedPositions.map(pos => start + pos);
          
          bestMatch = {
            start,
            end: start + windowSize - 1,
            matchedChars,
            score
          };
          
          // Early termination: if we found a perfect match (distance 0), we're done
          if (distance === 0) {
            return { 
              start: bestMatch.start, 
              end: bestMatch.end, 
              matchedChars: bestMatch.matchedChars 
            };
          }
        }
      }
    }
  }
  
  return bestMatch ? { 
    start: bestMatch.start, 
    end: bestMatch.end, 
    matchedChars: bestMatch.matchedChars 
  } : null;
}


/**
 * Fuzzy match algorithm - checks if query can match text with maxEdits edits
 * Returns true if a match is found
 */
function fuzzyMatch(text: string, query: string, maxEdits = 1): boolean {
  return findBestFuzzyMatch(text, query, maxEdits) !== null;
}

/**
 * Merge overlapping or adjacent ranges
 */
function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (ranges.length === 0) return [];
  
  // Sort by start position
  ranges.sort((a, b) => a[0] - b[0]);
  
  const merged: Array<[number, number]> = [ranges[0]];
  
  for (let i = 1; i < ranges.length; i++) {
    const current = ranges[i];
    const lastMerged = merged[merged.length - 1];
    
    // If current overlaps or is adjacent to lastMerged, merge them
    if (current[0] <= lastMerged[1] + 1) {
      lastMerged[1] = Math.max(lastMerged[1], current[1]);
    } else {
      merged.push(current);
    }
  }
  
  return merged;
}

/**
 * Highlight search terms for IDEAS matching the backend getIdeaSearchQueryObject logic
 * Supports:
 * 1. Exact match (case insensitive)
 * 2. Prefix match (word starts with query) - matches ${part}*
 * 3. Substring match (query appears anywhere in text) - matches wildcard *part*
 * 4. Fuzzy match (characters in order, maxEdits: 1-2 based on length)
 * 
 * This matches the search logic in getIdeaSearchQueryObject:
 * - itemKey: exact match only (not used for highlighting titles)
 * - title: exact (boost 3), prefix (boost 2.5), substring (boost 2), fuzzy (boost 1.5)
 * - description: exact (boost 1.5), prefix (boost 1.2), substring (boost 1), fuzzy (boost 0.8)
 */
export function highlightIdeaSearchText(text: string, searchQuery: string): React.ReactNode {
  if (!text || !searchQuery || searchQuery.trim().length < 2) {
    return text;
  }
  
  // Split query into parts (words) - same filter as backend
  const queryParts = searchQuery.trim().split(/\s+/).filter(part => /[a-zA-Z0-9]/.test(part));
  
  if (queryParts.length === 0) {
    return text;
  }
  
  // Collect all highlight ranges
  const highlightRanges: Array<[number, number]> = [];
  const textLower = text.toLowerCase();
  
  for (const part of queryParts) {
    if (part.length < 1) continue;
    
    const partLower = part.toLowerCase();
    
    // 1. Exact word match (matches text search on title - boost 3)
    const exactWordRegex = new RegExp(`\\b${escapeRegex(part)}`, 'gi');
    let match;
    while ((match = exactWordRegex.exec(text)) !== null) {
      highlightRanges.push([match.index, match.index + part.length - 1]);
    }
    
    // 2. Prefix match (matches ${part}* on title - boost 2.5)
    // Word starts with query part
    const prefixRegex = new RegExp(`\\b${escapeRegex(part)}`, 'gi');
    let prefixMatch: RegExpExecArray | null;
    while ((prefixMatch = prefixRegex.exec(text)) !== null) {
      // Only add if not already covered by exact match
      const alreadyCovered = highlightRanges.some(
        ([start, end]) => prefixMatch!.index >= start && prefixMatch!.index <= end
      );
      if (!alreadyCovered) {
        highlightRanges.push([prefixMatch.index, prefixMatch.index + part.length - 1]);
      }
    }
    
    // 3. Substring match (matches wildcard *part* on title - boost 2)
    // This allows "Invo" to match "Invoice"
    let substringIndex = textLower.indexOf(partLower);
    while (substringIndex !== -1) {
      // Only add if not already covered by exact or prefix match
      const alreadyCovered = highlightRanges.some(
        ([start, end]) => substringIndex >= start && substringIndex <= end
      );
      if (!alreadyCovered) {
        highlightRanges.push([substringIndex, substringIndex + part.length - 1]);
      }
      substringIndex = textLower.indexOf(partLower, substringIndex + 1);
    }
    
    // 4. Fuzzy match (for typos - boost 1.5)
    // maxEdits: part.length > 5 ? 2 : 1, prefixLength: 0
    // Only if no matches found yet
    const maxEditsForIdea = part.length > 5 ? 2 : 1;
    const fuzzyMatchResult = findBestFuzzyMatch(text, part, maxEditsForIdea);
    
    if (highlightRanges.length === 0 && fuzzyMatchResult) {
      const { start: fuzzyStart, end: fuzzyEnd, matchedChars } = fuzzyMatchResult;
      
      // For fuzzy matches, only highlight the characters that actually matched
      // (e.g., "jest" should only highlight "est" in "test")
      if (matchedChars.length > 0) {
        // Create ranges from matched character positions
        // Group consecutive matches into ranges
        let rangeStart = matchedChars[0];
        let rangeEnd = matchedChars[0];
        
        for (let i = 1; i < matchedChars.length; i++) {
          if (matchedChars[i] === rangeEnd + 1) {
            // Consecutive, extend range
            rangeEnd = matchedChars[i];
          } else {
            // Gap found, save current range and start new one
            highlightRanges.push([rangeStart, rangeEnd]);
            rangeStart = matchedChars[i];
            rangeEnd = matchedChars[i];
          }
        }
        
        // Add the last range
        highlightRanges.push([rangeStart, rangeEnd]);
      } else {
        // Fallback: if no matched chars but we have a match, highlight the whole range
        highlightRanges.push([fuzzyStart, fuzzyEnd]);
      }
    }
  }
  
  if (highlightRanges.length === 0) {
    return text;
  }
  
  // Merge overlapping ranges
  const mergedRanges = mergeRanges(highlightRanges);
  
  // Build the result with highlights
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  
  mergedRanges.forEach(([start, end], index) => {
    // Add text before highlight
    if (start > lastIndex) {
      result.push(text.substring(lastIndex, start));
    }
    
    // Add highlighted text
    result.push(
      <mark
        key={`highlight-${index}`}
        style={{
          backgroundColor: '#FEF3C7',
          color: '#92400E',
          padding: '0 2px',
          borderRadius: '2px',
          fontWeight: 500,
        }}
      >
        {text.substring(start, end + 1)}
      </mark>
    );
    
    lastIndex = end + 1;
  });
  
  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return <>{result}</>;
}

/**
 * Highlight search terms for EVIDENCES matching the backend getTextItemsSearchQueryObject logic
 * Supports:
 * 1. Exact match (case insensitive)
 * 2. Substring match (query appears anywhere in text) - matches wildcard *part*
 * 3. Fuzzy match (characters in order, maxEdits: 1, prefixLength: 0)
 * 
 * This matches the search logic in getTextItemsSearchQueryObject:
 * - title: text with fuzzy (boost 3) + wildcard *part* (boost 2)
 * - text: text with fuzzy + wildcard *part*
 * - excerpt: text with fuzzy + wildcard *part*
 * - Filters out query parts shorter than 2 characters
 */
export function highlightEvidenceSearchText(text: string, searchQuery: string): React.ReactNode {
  if (!text || !searchQuery || searchQuery.trim().length < 2) {
    return text;
  }
  
  // Split query into parts - filter out parts < 2 chars (same as backend)
  const queryParts = searchQuery.trim().split(/\s+/).filter(part => part.length >= 2);
  
  if (queryParts.length === 0) {
    return text;
  }
  
  // Collect all highlight ranges
  const highlightRanges: Array<[number, number]> = [];
  const textLower = text.toLowerCase();
  
  for (const part of queryParts) {
    const partLower = part.toLowerCase();
    
    // Track if we found exact or substring matches for this part
    let foundExactOrSubstring = false;
    
    // 1. Exact match (matches text search with fuzzy on title - boost 3)
    const exactWordRegex = new RegExp(`\\b${escapeRegex(part)}`, 'gi');
    let match;
    while ((match = exactWordRegex.exec(text)) !== null) {
      highlightRanges.push([match.index, match.index + part.length - 1]);
      foundExactOrSubstring = true;
    }
    
    // 2. Substring match (matches wildcard *part* on title - boost 2)
    // This allows "evid" to match "evidence"
    let substringIndex = textLower.indexOf(partLower);
    while (substringIndex !== -1) {
      // Only add if not already covered by exact match
      const alreadyCovered = highlightRanges.some(
        ([start, end]) => substringIndex >= start && substringIndex <= end
      );
      if (!alreadyCovered) {
        highlightRanges.push([substringIndex, substringIndex + part.length - 1]);
        foundExactOrSubstring = true;
      }
      substringIndex = textLower.indexOf(partLower, substringIndex + 1);
    }
    
    // 3. Fuzzy match (maxEdits: 1, prefixLength: 0)
    // Apply fuzzy matching for typos - highlight fuzzy matches even if exact matches exist
    // This handles cases where the query is a typo but matches similar text
    // MongoDB Atlas fuzzy matching allows up to 1 character edit (insertion, deletion, substitution)
    // Always try fuzzy matching to catch typos that don't match exact/substring
    const fuzzyMatchResult = findBestFuzzyMatch(text, part, 1);
    
    if (fuzzyMatchResult) {
      const { start: fuzzyStart, end: fuzzyEnd, matchedChars } = fuzzyMatchResult;
      
      // For fuzzy matches, only highlight the characters that actually matched
      // (e.g., "jest" should only highlight "est" in "test")
      if (matchedChars.length > 0) {
        // Create ranges from matched character positions
        // Group consecutive matches into ranges
        let rangeStart = matchedChars[0];
        let rangeEnd = matchedChars[0];
        
        for (let i = 1; i < matchedChars.length; i++) {
          if (matchedChars[i] === rangeEnd + 1) {
            // Consecutive, extend range
            rangeEnd = matchedChars[i];
          } else {
            // Gap found, save current range and start new one
            // Only add if not already covered by exact/substring match
            const alreadyCovered = foundExactOrSubstring && highlightRanges.some(
              ([start, end]) => rangeStart >= start && rangeEnd <= end
            );
            
            if (!alreadyCovered) {
              highlightRanges.push([rangeStart, rangeEnd]);
            }
            
            rangeStart = matchedChars[i];
            rangeEnd = matchedChars[i];
          }
        }
        
        // Add the last range
        const alreadyCovered = foundExactOrSubstring && highlightRanges.some(
          ([start, end]) => rangeStart >= start && rangeEnd <= end
        );
        
        if (!alreadyCovered) {
          highlightRanges.push([rangeStart, rangeEnd]);
        }
      } else if (!foundExactOrSubstring) {
        // Fallback: if no matched chars but we have a match, highlight the whole range
        highlightRanges.push([fuzzyStart, fuzzyEnd]);
      }
    }
  }
  
  if (highlightRanges.length === 0) {
    return text;
  }
  
  // Merge overlapping ranges
  const mergedRanges = mergeRanges(highlightRanges);
  
  // Build the result with highlights
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  
  mergedRanges.forEach(([start, end], index) => {
    // Add text before highlight
    if (start > lastIndex) {
      result.push(text.substring(lastIndex, start));
    }
    
    // Add highlighted text
    result.push(
      <mark
        key={`highlight-${index}`}
        style={{
          backgroundColor: '#FEF3C7',
          color: '#92400E',
          padding: '0 2px',
          borderRadius: '2px',
          fontWeight: 500,
        }}
      >
        {text.substring(start, end + 1)}
      </mark>
    );
    
    lastIndex = end + 1;
  });
  
  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return <>{result}</>;
}

/**
 * Escape special regex characters
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

